/** 核价预算后端 snake_case 与前端 camelCase 之间的映射。 */

import type { components } from "../../../api/generated";

/** 核价导入接口的生成契约仅在此适配边界接入。 */
export type BudgetImportPreview = components["schemas"]["BudgetImportPreview"];
export type BudgetImportRequest = components["schemas"]["BudgetImportRequest"];
export type BudgetImportResult = components["schemas"]["BudgetImportResult"];

import type {
  CopyVersionRequest,
  CopyVersionResult,
  EnterItineraryBudgetResult,
  FinalizeRequest,
  FinalizeResult,
  QuoteBudgetCandidateSummary,
  QuoteBudgetCostBreakdown,
  QuoteBudgetEditorData,
  QuoteBudgetEntryOptions,
  QuoteBudgetEntrySnapshot,
  QuoteBudgetItinerarySummary,
  QuoteBudgetHistorySearchResult,
  QuoteBudgetComparisonOptions,
  QuoteBudgetVersionCompareResult,
  QuoteBudgetHistorySource,
  QuoteBudgetLine,
  QuoteBudgetOrder,
  QuoteBudgetPriceCandidate,
  PriceReusePreview,
  QuoteBudgetPriceFallbackStep,
  QuoteBudgetTransportPoint,
  QuoteBudgetVersion,
  RebuildVersionRequest,
  SaveDraftLineInput,
  SaveDraftRequest,
} from "../types";

type Raw = Record<string, unknown>;

/** 转字符串，空值用兜底. */
function str(value: unknown, fallback = ""): string {
  return value === null || value === undefined ? fallback : String(value);
}

/** 转有限数值，非法值用兜底. */
function num(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** 转布尔，空值用兜底. */
function bool(value: unknown, fallback = false): boolean {
  return value === null || value === undefined ? fallback : Boolean(value);
}

/** 转可空字符串，空值返回 null. */
function optStr(value: unknown): string | null {
  return value === null || value === undefined ? null : String(value);
}

/** 历史响应兼容的行程数据缺口映射。 */
function mapRepairGap(raw: unknown): EnterItineraryBudgetResult["repairGap"] {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const gap = raw as Raw;
  const segmentId = optStr(gap.segment_id);
  return {
    gapId: str(gap.gap_id),
    gapType: str(gap.gap_type),
    dayNumber: num(gap.day_number),
    routeSeq: num(gap.route_seq),
    transportMode: str(gap.transport_mode),
    ...(segmentId ? { segmentId } : {}),
    fromNodeId: str(gap.from_node_id),
    fromName: str(gap.from_name),
    toNodeId: str(gap.to_node_id),
    toName: str(gap.to_name),
  };
}

/** 进入核价预算结果映射。 */
export function mapEnterResult(raw: Raw): EnterItineraryBudgetResult {
  const repairGap = mapRepairGap(raw.repair_gap);
  const repairGaps = Array.isArray(raw.repair_gaps)
    ? raw.repair_gaps.map(mapRepairGap).filter((gap): gap is NonNullable<typeof gap> => Boolean(gap))
    : [];
  return {
    status: str(raw.status, "ready") as EnterItineraryBudgetResult["status"],
    reasonCode: optStr(raw.reason_code),
    repairUrl: str(raw.repair_url),
    ...(repairGap ? { repairGap } : {}),
    ...(repairGaps.length > 0 ? { repairGaps } : {}),
    budgetOrderId: str(raw.budget_order_id),
    created: bool(raw.created),
    versionNo: num(raw.version_no),
    redirectTo: str(raw.redirect_to),
  };
}

/** 预算表头映射。 */
export function mapBudgetOrder(raw: Raw): QuoteBudgetOrder {
  return {
    budgetOrderId: str(raw.budget_order_id),
    budgetOrderNo: str(raw.budget_order_no),
    routeId: str(raw.route_id),
    versionNo: num(raw.version_no),
    versionName: str(raw.version_name),
    basedOnBudgetOrderId: optStr(raw.based_on_budget_order_id),
    studentCount: num(raw.student_count),
    teacherCount: num(raw.teacher_count),
    guideCount: num(raw.guide_count),
    driverCount: num(raw.driver_count),
    teacherPays: bool(raw.teacher_pays),
    targetMarginRate: str(raw.target_margin_rate, "0.2000"),
    costBudgetTotalAmount: str(raw.cost_budget_total_amount, "0.00"),
    perCapitaCostAmount: str(raw.per_capita_cost_amount, "0.00"),
    suggestedQuoteAmount: str(raw.suggested_quote_amount, "0.00"),
    status: str(raw.status, "draft"),
    lockedByCustomerQuoteId: optStr(raw.locked_by_customer_quote_id),
    customerQuoteLockedAt: optStr(raw.customer_quote_locked_at),
    remark: str(raw.remark),
  };
}

/** 受控快照直接保留内部 camelCase，不从文案猜测缺失因素。 */
function mapEntrySnapshot(value: unknown): QuoteBudgetEntrySnapshot | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "object" || Array.isArray(value)) throw new Error("人工录入因素快照无效");
  const raw = value as Raw;
  if (raw.schemaVersion !== 1 || raw.inputMode !== "manual_v2"
    || typeof raw.groupId !== "string" || !raw.groupId.trim()
    || typeof raw.groupLabel !== "string" || !raw.groupLabel.trim()
    || !["linked_people", "fixed_people", "quantity", "total"].includes(String(raw.quantityMode))
    || !Number.isInteger(raw.billingCount) || Number(raw.billingCount) <= 0) {
    throw new Error("人工录入因素快照无效");
  }
  return structuredClone(raw) as QuoteBudgetEntrySnapshot;
}

/** 预算行映射。 */
export function mapBudgetLine(raw: Raw): QuoteBudgetLine {
  return {
    budgetLineId: str(raw.budget_line_id),
    budgetOrderId: str(raw.budget_order_id),
    lineNo: num(raw.line_no),
    resourceType: str(raw.resource_type),
    resourceEntryType: str(raw.resource_entry_type, "manual"),
    spuTableName: optStr(raw.spu_table_name),
    spuId: optStr(raw.spu_id),
    spuNameSnapshot: str(raw.spu_name_snapshot),
    skuTableName: optStr(raw.sku_table_name),
    skuId: optStr(raw.sku_id),
    skuNameSnapshot: str(raw.sku_name_snapshot),
    itemNameSnapshot: str(raw.item_name_snapshot),
    itemSpecSnapshot: str(raw.item_spec_snapshot),
    travelerRole: optStr(raw.traveler_role),
    mealType: optStr(raw.meal_type),
    calcType: str(raw.calc_type, "snapshot"),
    peopleMode: str(raw.people_mode, "manual"),
    manualPeopleCount: optStr(raw.manual_people_count),
    isLinkedToPeopleCount: bool(raw.is_linked_to_people_count, true),
    isCapitaShared: bool(raw.is_capita_shared, true),
    isAgentCollection: bool(raw.is_agent_collection, str(raw.resource_type) === "大交通"),
    isLocked: bool(raw.is_locked),
    quantity: str(raw.quantity, "0"),
    unit: str(raw.unit),
    dayCount: raw.day_count === null || raw.day_count === undefined ? null : num(raw.day_count),
    nightCount:
      raw.night_count === null || raw.night_count === undefined ? null : num(raw.night_count),
    budgetUnitPrice: str(raw.budget_unit_price, "0"),
    budgetAmount: str(raw.budget_amount, "0"),
    priceSourceType: str(raw.price_source_type, "manual"),
    priceSourceRef: optStr(raw.price_source_ref),
    priceBasisDesc: str(raw.price_basis_desc),
    entrySnapshot: mapEntrySnapshot(raw.entry_snapshot),
    remark: str(raw.remark),
  };
}

/** 版本列表项映射。 */
export function mapVersion(raw: Raw): QuoteBudgetVersion {
  return {
    budgetOrderId: str(raw.budget_order_id),
    versionNo: num(raw.version_no),
    versionName: str(raw.version_name),
    status: str(raw.status),
    costBudgetTotalAmount: str(raw.cost_budget_total_amount, "0.00"),
    basedOnBudgetOrderId: optStr(raw.based_on_budget_order_id),
    isLatest: bool(raw.is_latest),
    createdAt: optStr(raw.created_at),
    updatedAt: optStr(raw.updated_at),
  };
}

/** 行程摘要映射. */
function mapItinerarySummary(raw: Raw | null | undefined): QuoteBudgetItinerarySummary | null {
  if (!raw) return null;
  return {
    itineraryId: str(raw.itinerary_id),
    title: str(raw.title),
    startDate: optStr(raw.start_date),
    endDate: optStr(raw.end_date),
    status: optStr(raw.status),
    stage: optStr(raw.stage),
  };
}

/** 跨行程历史核价来源映射。 */
function mapHistorySource(raw: Raw | null | undefined): QuoteBudgetHistorySource | null {
  if (!raw) return null;
  return {
    itineraryId: str(raw.itinerary_id),
    itineraryTitle: str(raw.itinerary_title),
    budgetOrderId: str(raw.budget_order_id),
    budgetOrderNo: str(raw.budget_order_no),
    versionNo: num(raw.version_no),
    versionName: str(raw.version_name),
  };
}

/** 历史核价标题查询结果映射。 */
export function mapHistoryBudgetSearchResult(raw: Raw): QuoteBudgetHistorySearchResult {
  const items = Array.isArray(raw.items) ? (raw.items as Raw[]) : [];
  return {
    items: items.map((item) => ({
      itineraryId: str(item.itinerary_id),
      itineraryTitle: str(item.itinerary_title),
      startDate: optStr(item.start_date),
      endDate: optStr(item.end_date),
      versions: (Array.isArray(item.versions) ? (item.versions as Raw[]) : []).map((version) => ({
        budgetOrderId: str(version.budget_order_id),
        budgetOrderNo: str(version.budget_order_no),
        versionNo: num(version.version_no),
        versionName: str(version.version_name),
        costBudgetTotalAmount: str(version.cost_budget_total_amount, "0.00"),
        updatedAt: optStr(version.updated_at),
      })),
    })),
    limited: bool(raw.limited),
  };
}

/** 核价版本比较结果映射。 */
export function mapVersionCompareResult(raw: Raw): QuoteBudgetVersionCompareResult {
  if (typeof raw.summary !== "string" || !raw.summary.trim()) {
    throw new Error("差异说明缺失，请重新生成");
  }
  return {
    left: mapEditorData((raw.left as Raw) ?? {}),
    right: mapEditorData((raw.right as Raw) ?? {}),
    summary: raw.summary.trim(),
  };
}

/** 比较候选独立映射，避免继承历史复制的定稿限制。 */
export function mapComparisonOptions(raw: Raw): QuoteBudgetComparisonOptions {
  const groups = mapHistoryBudgetSearchResult(raw);
  const items = Array.isArray(raw.items) ? raw.items as Raw[] : [];
  return {
    ...groups,
    items: groups.items.map((group, index) => ({
      ...group,
      versions: ((items[index]?.versions ?? []) as Raw[]).map(mapVersion),
    })),
  };
}

/** 候选行摘要计数映射. */
function mapCandidateSummary(raw: Raw | null | undefined): QuoteBudgetCandidateSummary {
  return {
    totalCount: num(raw?.total_count),
    missingSkuCount: num(raw?.missing_sku_count),
    missingPriceCount: num(raw?.missing_price_count),
    unconfirmedCount: num(raw?.unconfirmed_count),
  };
}

/** 自然语言录入候选映射。 */
function mapEntryOptions(raw: Raw | null | undefined): QuoteBudgetEntryOptions {
  const stayAreas = Array.isArray(raw?.stay_areas) ? (raw.stay_areas as Raw[]) : [];
  const points = Array.isArray(raw?.points) ? (raw.points as Raw[]) : [];
  const transports = Array.isArray(raw?.transports) ? (raw.transports as Raw[]) : [];
  return {
    itineraryDays: (Array.isArray(raw?.itinerary_days) ? raw.itinerary_days as Raw[] : []).map((day) => ({
      day: num(day.day),
      ...(optStr(day.province_name) ? { provinceName: str(day.province_name) } : {}),
      ...(optStr(day.city_name) ? { cityName: str(day.city_name) } : {}),
      ...(optStr(day.transfer) ? { transfer: str(day.transfer) } : {}),
      ...(Array.isArray(day.regions) ? { regions: (day.regions as Raw[]).map(region => ({
        ...(optStr(region.province_name) ? { provinceName: str(region.province_name) } : {}),
        ...(optStr(region.city_name) ? { cityName: str(region.city_name) } : {}),
      })) } : {}),
    })),
    stayAreas: stayAreas.map((item) => {
      const recommendedPrice = optStr(item.recommended_price);
      return {
        id: optStr(item.id),
        name: str(item.name),
        nightCount: num(item.night_count),
        items: (Array.isArray(item.items) ? item.items as Raw[] : []).map((sku) => ({
          spuId: str(sku.spu_id),
          skuId: str(sku.sku_id),
          skuName: str(sku.sku_name),
          accommodationRole: str(sku.accommodation_role) as "student" | "teacher" | "guide" | "driver",
          stayLevel: str(sku.stay_level),
          settlementUnit: str(sku.settlement_unit),
          ...(optStr(sku.recommended_price) ? { recommendedPrice: str(sku.recommended_price) } : {}),
          selectedScope: str(sku.selected_scope, "no_sample"),
        })),
        ...(recommendedPrice ? { recommendedPrice } : {}),
      };
    }),
    points: points.map((item) => {
      const pointItems = Array.isArray(item.items) ? (item.items as Raw[]) : [];
      const items = pointItems.flatMap((pointItem) => {
        const ticketResourceType = str(pointItem.ticket_resource_type);
        const travelerRole = str(pointItem.traveler_role);
        if (
          !ticketResourceType.trim()
          || !["student", "teacher"].includes(travelerRole)
        ) {
          return [];
        }
        const recommendedPrice = optStr(pointItem.recommended_price);
        return [{
          skuId: str(pointItem.sku_id),
          skuName: str(pointItem.sku_name),
          ticketResourceType,
          travelerRole: travelerRole as "student" | "teacher",
          settlementUnit: str(pointItem.settlement_unit, "人"),
          ...(recommendedPrice ? { recommendedPrice } : {}),
          selectedScope: str(pointItem.selected_scope, "no_sample"),
        }];
      });
      return {
        id: optStr(item.id),
        name: str(item.name),
        dayNumbers: [...new Set((Array.isArray(item.day_numbers) ? item.day_numbers : []).filter((day): day is number => typeof day === "number" && Number.isSafeInteger(day) && day > 0))].sort((left, right) => left - right),
        ...(items.length > 0 ? { items } : {}),
      };
    }),
    transports: transports.flatMap((item) => {
      const mode = str(item.mode);
      if (!["air", "rail"].includes(mode)) return [];
      const recommendedPrice = optStr(item.recommended_price);
      return [{
        mode: mode as "air" | "rail",
        routeCode: str(item.route_code),
        fromName: str(item.from_name),
        toName: str(item.to_name),
        seatClass: str(item.seat_class),
        ...(recommendedPrice ? { recommendedPrice } : {}),
      }];
    }),
  };
}

const COST_BUCKET_KEYS = new Set(["student", "teacher", "guide", "driver", "unassigned"]);
const COST_AUDIT_CODES = new Set([
  "order_total_mismatch",
  "per_capita_mismatch",
  "point_resource_type_mismatch",
]);
const COST_AUDIT_SEVERITIES = new Set(["warning", "error"]);

/** 判断成本桶键是否属于当前前端支持的后端契约。 */
function isCostBucketKey(
  value: string,
): value is QuoteBudgetCostBreakdown["buckets"][number]["key"] {
  return COST_BUCKET_KEYS.has(value);
}

/** 判断审计编码是否属于当前前端支持的后端契约。 */
function isCostAuditCode(
  value: string,
): value is QuoteBudgetCostBreakdown["auditIssues"][number]["code"] {
  return COST_AUDIT_CODES.has(value);
}

/** 判断审计级别是否属于当前前端支持的后端契约。 */
function isCostAuditSeverity(
  value: string,
): value is QuoteBudgetCostBreakdown["auditIssues"][number]["severity"] {
  return COST_AUDIT_SEVERITIES.has(value);
}

/** 服务端成本构成与审计问题映射；缺失或未知枚举不伪造成合法快照。 */
function mapCostBreakdown(raw: Raw | null | undefined): QuoteBudgetCostBreakdown | undefined {
  if (!raw) return undefined;
  const buckets = Array.isArray(raw?.buckets) ? (raw.buckets as Raw[]) : [];
  const auditIssues = Array.isArray(raw?.audit_issues) ? (raw.audit_issues as Raw[]) : [];
  return {
    ordinaryAmount: str(raw?.ordinary_amount, "0.00"),
    businessTaxAmount: str(raw?.business_tax_amount, "0.00"),
    totalAmount: str(raw?.total_amount, "0.00"),
    shareCount: num(raw?.share_count),
    perCapitaAmount: str(raw?.per_capita_amount, "0.00"),
    buckets: buckets.flatMap((item) => {
      const key = str(item.key);
      return isCostBucketKey(key)
        ? [{ key, amount: str(item.amount, "0.00"), lineCount: num(item.line_count) }]
        : [];
    }),
    auditIssues: auditIssues.flatMap((item) => {
      const code = str(item.code);
      const severity = str(item.severity);
      return isCostAuditCode(code) && isCostAuditSeverity(severity)
        ? [{
            code,
            severity,
            budgetLineId: optStr(item.budget_line_id),
            lineNo: item.line_no === null || item.line_no === undefined ? null : num(item.line_no),
            message: str(item.message),
          }]
        : [];
    }),
  };
}

/** 大交通起终点候选映射。 */
export function mapTransportPoint(raw: Raw): QuoteBudgetTransportPoint {
  return {
    pointId: str(raw.point_id),
    name: str(raw.name),
    shortName: optStr(raw.short_name),
    pointType: str(raw.point_type),
    city: optStr(raw.city),
  };
}

/** 编辑器查询数据映射。 */
export function mapEditorData(raw: Raw): QuoteBudgetEditorData {
  const revision = typeof raw.revision === "string" ? raw.revision.trim() : "";
  if (!revision) throw new Error("核价修订快照缺失或无效");
  const lines = Array.isArray(raw.lines) ? (raw.lines as Raw[]) : [];
  const versions = Array.isArray(raw.versions) ? (raw.versions as Raw[]) : [];
  return {
    revision,
    budgetOrder: mapBudgetOrder((raw.budget_order as Raw) ?? {}),
    lines: lines.map(mapBudgetLine),
    versions: versions.map(mapVersion),
    itinerarySummary: mapItinerarySummary(raw.itinerary_summary as Raw | null),
    candidateSummary: mapCandidateSummary(raw.candidate_summary as Raw | null),
    entryOptions: mapEntryOptions(raw.entry_options as Raw | null),
    costBreakdown: mapCostBreakdown(raw.cost_breakdown as Raw | null),
    historySource: mapHistorySource(raw.history_source as Raw | null),
    readonly: bool(raw.readonly),
    readonlyReason: optStr(raw.readonly_reason),
  };
}

/** 推荐价降级链单档映射. */
function mapPriceFallbackStep(raw: Raw): QuoteBudgetPriceFallbackStep {
  return {
    scope: str(raw.scope),
    sampleCount: num(raw.sample_count),
    minSamples: num(raw.min_samples),
    selected: bool(raw.selected),
  };
}

/** 推荐价映射。 */
export function mapPriceCandidate(raw: Raw): QuoteBudgetPriceCandidate {
  const chain = Array.isArray(raw.fallback_chain) ? (raw.fallback_chain as Raw[]) : [];
  return {
    skuTableName: optStr(raw.sku_table_name),
    skuId: optStr(raw.sku_id),
    serviceMonth:
      raw.service_month === null || raw.service_month === undefined
        ? null
        : num(raw.service_month),
    selectedScope: str(raw.selected_scope, "no_sample"),
    recommendedPrice: optStr(raw.recommended_price),
    p50Price: optStr(raw.p50_price),
    minPrice: optStr(raw.min_price),
    avgPrice: optStr(raw.avg_price),
    maxPrice: optStr(raw.max_price),
    sampleCount: num(raw.sample_count),
    fallbackChain: chain.map(mapPriceFallbackStep),
    riskCode: optStr(raw.risk_code),
  };
}

/** 复制版本结果映射。 */
export function mapCopyVersionResult(raw: Raw): CopyVersionResult {
  return {
    budgetOrderId: str(raw.budget_order_id),
    versionNo: num(raw.version_no),
    basedOnBudgetOrderId: optStr(raw.based_on_budget_order_id),
    reusedPriceCount: num(raw.reused_price_count),
    historySource: mapHistorySource(raw.history_source as Raw | null),
  };
}

/** 基于最新行程重建前的精确价格复用预览映射。 */
export function mapPriceReusePreview(raw: Raw): PriceReusePreview {
  const items = Array.isArray(raw.items) ? (raw.items as Raw[]) : [];
  const mapIssues = (value: unknown) => (Array.isArray(value) ? (value as Raw[]) : []).map((item) => ({
    targetLineNo: num(item.target_line_no),
    resourceType: str(item.resource_type),
    itemNameSnapshot: str(item.item_name_snapshot),
    itemSpecSnapshot: str(item.item_spec_snapshot),
    travelerRole: optStr(item.traveler_role),
    reasonCode: str(item.reason_code),
    reason: str(item.reason, "未提供逐项原因，请核对该明细。"),
  }));
  return {
    matchedCount: num(raw.matched_count),
    unmatchedCount: num(raw.unmatched_count),
    ambiguousCount: num(raw.ambiguous_count),
    ...(Array.isArray(raw.unmatched_items) ? { unmatchedItems: mapIssues(raw.unmatched_items) } : {}),
    ...(Array.isArray(raw.ambiguous_items) ? { ambiguousItems: mapIssues(raw.ambiguous_items) } : {}),
    items: items.flatMap((item) => {
      const matchIdentityType = str(item.match_identity_type);
      return matchIdentityType === "sku" || matchIdentityType === "spu"
        ? [{
            targetLineNo: num(item.target_line_no),
            resourceType: str(item.resource_type),
            itemNameSnapshot: str(item.item_name_snapshot),
            itemSpecSnapshot: str(item.item_spec_snapshot),
            travelerRole: optStr(item.traveler_role),
            unit: str(item.unit),
            currentUnitPrice: str(item.current_unit_price, "0.00"),
            sourceUnitPrice: str(item.source_unit_price, "0.00"),
            matchIdentityType,
          }]
        : [];
    }),
  };
}

/** 定稿结果映射。 */
export function mapFinalizeResult(raw: Raw): FinalizeResult {
  return {
    budgetOrderId: str(raw.budget_order_id),
    status: str(raw.status),
    costBudgetTotalAmount: str(raw.cost_budget_total_amount, "0.00"),
  };
}

/** 单行保存入参转 snake_case，不含 budget_amount. */
function mapSaveDraftLine(line: SaveDraftLineInput): Raw {
  // 不提交 budget_amount，金额由后端按 quantity * budget_unit_price 重算。
  return {
    budget_line_id: line.budgetLineId ?? null,
    ...(line.replacesLineIds?.length ? { replaces_line_ids: line.replacesLineIds } : {}),
    ...(line.recalculateLegacyBusAmount ? { recalculate_legacy_bus_amount: true } : {}),
    line_no: line.lineNo,
    resource_type: line.resourceType,
    resource_entry_type: line.resourceEntryType ?? "manual",
    spu_table_name: line.spuTableName ?? null,
    spu_id: line.spuId ?? null,
    spu_name_snapshot: line.spuNameSnapshot ?? "",
    sku_table_name: line.skuTableName ?? null,
    sku_id: line.skuId ?? null,
    sku_name_snapshot: line.skuNameSnapshot ?? "",
    item_name_snapshot: line.itemNameSnapshot ?? "",
    item_spec_snapshot: line.itemSpecSnapshot ?? "",
    traveler_role: line.travelerRole ?? null,
    meal_type: line.mealType ?? null,
    calc_type: line.calcType ?? "snapshot",
    people_mode: line.peopleMode ?? "manual",
    manual_people_count: line.manualPeopleCount ?? null,
    is_linked_to_people_count: line.isLinkedToPeopleCount ?? true,
    is_capita_shared: line.isCapitaShared ?? true,
    is_agent_collection: line.isAgentCollection ?? line.resourceType === "大交通",
    is_locked: line.isLocked ?? false,
    quantity: str(line.quantity, "0"),
    unit: line.unit,
    day_count: line.dayCount ?? null,
    night_count: line.nightCount ?? null,
    budget_unit_price: str(line.budgetUnitPrice, "0"),
    price_source_type: line.priceSourceType ?? "manual",
    price_source_ref: line.priceSourceRef ?? null,
    price_basis_desc: line.priceBasisDesc ?? "",
    entry_snapshot: line.entrySnapshot ?? null,
    remark: line.remark ?? "",
  };
}

/** 保存草稿请求体映射。 */
export function mapSaveDraftPayload(request: SaveDraftRequest): Raw {
  return {
    expected_revision: request.expectedRevision,
    version_name: request.versionName ?? null,
    student_count: request.studentCount,
    teacher_count: request.teacherCount,
    guide_count: request.guideCount,
    driver_count: request.driverCount,
    teacher_pays: request.teacherPays,
    target_margin_rate: request.targetMarginRate ?? null,
    remark: request.remark ?? "",
    lines: request.lines.map(mapSaveDraftLine),
  };
}

/** 复制版本请求体映射。 */
export function mapCopyVersionPayload(request: CopyVersionRequest): Raw {
  return {
    version_name: request.versionName ?? undefined,
    idempotency_key: request.idempotencyKey ?? undefined,
  };
}

/** 按最新行程重建版本请求体映射。 */
export function mapRebuildVersionPayload(request: RebuildVersionRequest): Raw {
  return {
    ...mapCopyVersionPayload(request),
    reuse_exact_matched_prices: request.reuseExactMatchedPrices ?? false,
  };
}

/** 定稿请求体映射。 */
export function mapFinalizePayload(request: FinalizeRequest): Raw {
  return {
    confirm_risk_codes: request.confirmRiskCodes,
    ...(request.versionName ? { version_name: request.versionName } : {}),
  };
}
